/**
 * ToolsBrowser Component
 *
 * Main modal for browsing and selecting custom/builtin tools
 * Migrated from tool-browser.js createCustom() and HTML templates
 */

import React from 'react';
import { Modal, Loading, ErrorMessage } from '../../../components/common';
import { ToolsList } from './ToolsList';
import { SelectedToolsList } from './SelectedToolsList';
import { useToolsBrowser } from '../hooks';
import { useI18n } from '@/i18n';
import type { ToolMode, CustomToolsConfig } from '../../../types/tools';

export interface ToolsBrowserProps {
  isOpen: boolean;
  mode: ToolMode;
  initialConfig?: CustomToolsConfig | string;
  onClose: () => void;
  onApply: (config: string) => void;
}

export const ToolsBrowser: React.FC<ToolsBrowserProps> = ({
  isOpen,
  mode,
  initialConfig,
  onClose,
  onApply,
}) => {
  const { messages } = useI18n();
  const t = messages.agentConfig.toolsBrowser;
  const {
    // State
    filterCategory,
    setFilterCategory,

    // Data
    categories,
    filteredBuiltinToolkits,
    groupedCustomTools,

    // Computed
    selectedToolCount,
    browserTitle,
    generatedConfig,

    // Validation
    invalidTools,
    hasInvalidTools,

    // Loading/Error
    isLoading,
    error,

    // Actions
    toggleTool,
    toggleModule,
    toggleSubTool,
    toggleCategory,
    isToolEnabled,
    isModuleSelected,
    isSubToolEnabled,
    isCategorySelected,
    handleApply,
    enabledTools,
    enabledSubTools,
    getDisplayName,
  } = useToolsBrowser({
    mode,
    initialConfig,
    onApply,
  });

  const tExtended = t as Record<string, string> | undefined;

  const handleApplyClick = () => {
    handleApply();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={browserTitle}
      size="6xl"
    >
      <div className="space-y-4">
        {/* Mode indicator - show which type of tools we're browsing */}
        <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            mode === 'custom'
              ? 'bg-green-100 text-green-800'
              : 'bg-blue-100 text-blue-800'
          }`}>
            {mode === 'custom' ? t?.customTools : t?.builtinToolkits}
          </span>
          <span className="text-sm text-gray-500">
            {mode === 'custom'
              ? t?.customToolsDesc
              : t?.builtinToolkitsDesc}
          </span>
        </div>

        {/* Category filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">{t?.filterByCategory}</label>
          <select
            value={filterCategory || ''}
            onChange={(e) => setFilterCategory(e.target.value || null)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{t?.allCategories}</option>
            {(mode === 'builtin' ? categories.builtin : categories.custom).map(
              (cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              )
            )}
          </select>

          {/* Selected count */}
          <span className="ml-auto text-sm text-gray-600">
            {t?.toolsSelected?.replace('{count}', String(selectedToolCount))}
          </span>
        </div>

        {/* Loading state */}
        {isLoading && <Loading message={t?.loadingTools} />}

        {/* Error state */}
        {error && <ErrorMessage error={error} />}

        {/* Content */}
        {!isLoading && !error && (
          <div className="grid grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto">
            {/* Left: Available tools (2 columns) */}
            <div className="col-span-2 space-y-4">
              <h4 className="font-semibold text-gray-900">{t?.availableTools}</h4>
              <ToolsList
                type={mode}
                builtinToolkits={mode === 'builtin' ? filteredBuiltinToolkits : []}
                customToolGroups={mode === 'custom' ? groupedCustomTools : []}
                isModuleSelected={isModuleSelected}
                isToolEnabled={isToolEnabled}
                isSubToolEnabled={isSubToolEnabled}
                isCategorySelected={isCategorySelected}
                onToggleModule={toggleModule}
                onToggleTool={toggleTool}
                onToggleSubTool={toggleSubTool}
                onToggleCategory={toggleCategory}
                getDisplayName={getDisplayName}
              />
            </div>

            {/* Right: Selected tools (1 column) */}
            <div className="col-span-1 border-l border-gray-200 pl-4">
              <h4 className="font-semibold text-gray-900 mb-3">{t?.selectedTools}</h4>
              {/* Warning for invalid tools */}
              {hasInvalidTools && (
                <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-700">
                    {tExtended?.invalidToolsWarning ||
                      "Some selected tools no longer exist. Please remove them before applying."}
                  </p>
                </div>
              )}
              {mode === 'builtin' ? (
                <div className="space-y-4">
                  {/* Toolkits */}
                  <div>
                    <h5 className="text-xs font-medium text-gray-500 mb-2 uppercase">Toolkits</h5>
                    <SelectedToolsList
                      selectedTools={enabledTools}
                      onRemove={toggleModule}
                      getDisplayName={getDisplayName}
                      invalidTools={invalidTools}
                    />
                  </div>
                  {/* Sub-tools */}
                  {enabledSubTools.length > 0 && (
                    <div>
                      <h5 className="text-xs font-medium text-gray-500 mb-2 uppercase">Sub-tools</h5>
                      <SelectedToolsList
                        selectedTools={enabledSubTools}
                        onRemove={(fullName) => {
                          // Extract toolkit name from full_name (e.g., "reasoning.think" -> "builtin:reasoning")
                          const toolkitName = fullName.split('.')[0];
                          toggleSubTool(fullName, `builtin:${toolkitName}`);
                        }}
                        getDisplayName={getDisplayName}
                        invalidTools={invalidTools}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <SelectedToolsList
                  selectedTools={enabledTools}
                  onRemove={toggleTool}
                  getDisplayName={getDisplayName}
                  invalidTools={invalidTools}
                />
              )}
            </div>
          </div>
        )}

        {/* Preview generated config */}
        {generatedConfig && (
          <div className="border-t border-gray-200 pt-4">
            <details className="group">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                {t?.previewConfig}
              </summary>
              <pre className="mt-2 p-3 bg-gray-50 rounded text-xs overflow-x-auto">
                {generatedConfig}
              </pre>
            </details>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
          >
            {t?.cancel}
          </button>
          <button
            onClick={handleApplyClick}
            disabled={hasInvalidTools}
            className={`px-4 py-2 rounded transition-colors ${
              hasInvalidTools
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-green-500 text-white hover:bg-green-600"
            }`}
            title={hasInvalidTools ? tExtended?.removeInvalidToolsFirst || "Remove invalid tools first" : undefined}
          >
            {t?.apply}
          </button>
        </div>
      </div>
    </Modal>
  );
};
